import React, { useMemo, useRef, useState } from "react";
import { useMoney } from "../../../hooks/useMoney";
import { DistributorSummaryItem } from "../types";
import { MISChart } from "../components/MISChart";
import { chartTheme } from "../chartTheme";
import { SectionToggle, SectionViewMode } from "../components/SectionToggle";
import { TablePagination } from "../components/TablePagination";
import { ExportButton } from "../components/ExportButton";
import { downloadChartImage, exportToCsv } from "../components/exportUtils";
import type { Chart as ChartJS } from "chart.js";

interface DistributorSummarySectionProps {
  data: DistributorSummaryItem[];
}

const PAGE_SIZE = 10;

export const DistributorSummarySection: React.FC<DistributorSummarySectionProps> = ({ data }) => {
  const { code, number } = useMoney();
  const [mode, setMode] = useState<SectionViewMode>("table");
  const [page, setPage] = useState(1);
  const chartRef = useRef<ChartJS | null>(null);
  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const visible = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return data.slice(startIndex, startIndex + PAGE_SIZE);
  }, [page, data]);

  if (!data.length) return null;

  const handleExportChart = () => {
    downloadChartImage(chartRef.current, "distributor-summary.png");
  };

  const handleExportTable = () => {
    exportToCsv(
      "distributor-summary.csv",
      [
        "Distributor",
        "Issued",
        "Activated",
        "Used",
        "Expired",
        "Revoked",
        "Issued Amt",
        "Activated Amt",
        "Used Amt",
      ],
      data.map((row) => [
        row.distributorName,
        row.issued,
        row.activated,
        row.used,
        row.expired,
        row.revoked,
        row.issuedAmt ?? 0,
        row.activatedAmt ?? 0,
        row.usedAmt ?? 0,
      ])
    );
  };

  return (
    <div className="overflow-hidden border border-cardborder bg-white shadow-sm">
      <div className="border-b border-cardborder bg-gray-50 px-6 py-4">
      <SectionToggle
        title="Distributor Summary"
        subtitle="Voucher counts and amounts by distributor"
        mode={mode}
        onChange={setMode}
        actions={
          <ExportButton
            label={mode === "graph" ? "Export PNG" : "Export CSV"}
            onClick={mode === "graph" ? handleExportChart : handleExportTable}
          />
        }
      />
      </div>
      <div className="px-6 py-6">
      {mode === "graph" ? (
        (() => {
          const filtered = data.filter((x) => x.issued > 0 || x.used > 0);
          return filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No data to display</p>
          ) : (
            <MISChart
              ref={chartRef}
              labels={filtered.map((x) => x.distributorName)}
              datasets={[
                {
                  label: "Issued",
                  data: filtered.map((x) => x.issued),
                  borderColor: chartTheme.brand,
                  backgroundColor: chartTheme.brandLight,
                },
                {
                  label: "Used",
                  data: filtered.map((x) => x.used),
                  borderColor: chartTheme.indigo,
                  backgroundColor: chartTheme.indigoLight,
                },
              ]}
              type="bar"
              options={{
                scales: {
                  x: { stacked: true },
                  y: { stacked: true },
                },
              }}
            />
          );
        })()
      ) : (
        <div className="overflow-hidden border border-cardborder">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Distributor</th>
                  <th className="px-3 py-2 text-right font-semibold">Issued</th>
                  <th className="px-3 py-2 text-right font-semibold">Activated</th>
                  <th className="px-3 py-2 text-right font-semibold">Used</th>
                  <th className="px-3 py-2 text-right font-semibold">Expired</th>
                  <th className="px-3 py-2 text-right font-semibold">Revoked</th>
                  <th className="px-3 py-2 text-right font-semibold">Issued Amt</th>
                  <th className="px-3 py-2 text-right font-semibold">Activated Amt</th>
                  <th className="px-3 py-2 text-right font-semibold">Used Amt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visible.map((row, idx) => (
                  <tr
                    key={row.distributorId}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} transition-colors hover:bg-brand/5`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {row.distributorName}
                    </td>
                    <td className="px-3 py-2 text-right">{row.issued}</td>
                    <td className="px-3 py-2 text-right">{row.activated}</td>
                    <td className="px-3 py-2 text-right">{row.used}</td>
                    <td className="px-3 py-2 text-right">{row.expired}</td>
                    <td className="px-3 py-2 text-right">{row.revoked}</td>
                    <td className="px-3 py-2 text-right">
                      {code} {number((row.issuedAmt ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {code} {number((row.activatedAmt ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {code} {number((row.usedAmt ?? 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={data.length}
            pageSize={PAGE_SIZE}
            itemLabel="distributors"
            onPageChange={setPage}
          />
        </div>
      )}
      </div>
    </div>
  );
};
